import React, { ReactNode } from 'react';
import cx from 'classnames';

import { getInitialsFromName } from '@trqk/utils';

import './avatar-halo.css';

interface Props {
    color: ColorGrade;
    imageUrl?: string;
    label?: ReactNode;
    name?: string;
}

const dotWidth = 48;
const haloWidth = 60;
const dotStyle = {
    width: dotWidth,
    height: dotWidth,
    borderRadius: '50%',
};
const haloStyle = { width: haloWidth, height: haloWidth, borderRadius: '50%' };

/**
 * Displays a round icon for a user, using a solid circle (a.k.a. "dot") in the center surrounded by
 * a ring of gradient (a.k.a. a "halo"). Both the dot and halo are styled using the `color` prop.
 * If an imageUrl is provided, the image is used as the icon.
 * If an imageUrl is not provided, the name is converted to first and last initials and these are
 * displayed over a solid background. If a name is not provided, the label is used instead.
 */
export const AvatarHalo = ({
    color,
    imageUrl,
    label,
    name,
}: Props): JSX.Element => {
    const dotClassNames = `rounded-full flex justify-center items-center text-white text-lg font-semibold bg-${color}-500 align-middle`;
    const haloClassNames = cx(
        'avatar-halo rounded-full flex justify-center items-center',
        color,
    );

    return (
        <div className={haloClassNames} style={haloStyle}>
            {imageUrl && (
                <img
                    src={imageUrl}
                    alt={`Avatar for ${name}`}
                    className={dotClassNames}
                    style={dotStyle}
                />
            )}
            {!imageUrl && (
                <div
                    className={cx(dotClassNames, color)}
                    style={{
                        width: dotWidth,
                        height: dotWidth,
                    }}
                >
                    {!label && name && <>{getInitialsFromName(name)}</>}
                    {label && <>{label}</>}
                </div>
            )}
        </div>
    );
};
